import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {MaterialModule} from '../../../../material.module';
import {
  ConnectInternalAiTool,
  ConnectInternalAiToolsService
} from '../../../../services/connect/connect-internal-ai-tools.service';
import {IWorkflowFunctionBinding} from '../../../../models/function-options.model';

type WorkflowBindingRow = {
  rowId: string;
  operationId: string;
  workflowId: string;
  workflowName: string;
  workflowVersion: string;
  specHash: string;
  description: string;
  inputSchema: string;
  runtimeReady: boolean;
  blockingReasons: string[];
  missing: boolean;
};

@Component({
  selector: 'app-function-workflow-binding-selector',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './function-workflow-binding-selector.component.html',
  styleUrls: ['./function-workflow-binding-selector.component.scss']
})
export class FunctionWorkflowBindingSelectorComponent implements OnInit {
  private readonly toolsService = inject(ConnectInternalAiToolsService);

  @Input() binding: IWorkflowFunctionBinding | null = null;
  @Output() bindingChange = new EventEmitter<IWorkflowFunctionBinding | null>();

  loading = false;
  error: string | null = null;
  rows: WorkflowBindingRow[] = [];

  ngOnInit(): void {
    this.loadRows();
  }

  selectedOperationId(): string {
    return this.binding?.operationId ?? '';
  }

  select(row: WorkflowBindingRow): void {
    if (!row.runtimeReady || row.missing) {
      return;
    }

    this.binding = this.toBinding(row);
    this.bindingChange.emit(this.binding);
  }

  private toBinding(row: WorkflowBindingRow): IWorkflowFunctionBinding {
    return {
      operationId: row.operationId,
      workflowId: row.workflowId,
      workflowVersion: row.workflowVersion,
      specHash: row.specHash,
      inputSchema: row.inputSchema,
      display: {
        toolName: row.operationId,
        description: row.description
      },
      wait: {
        executionMode: 'wait',
        waitTimeoutMs: 0
      },
      availability: {
        runtimeReady: row.runtimeReady,
        missing: row.missing,
        blockingReasons: [...row.blockingReasons],
        message: this.availabilityMessage(row)
      }
    };
  }

  clearSelection(): void {
    this.binding = null;
    this.bindingChange.emit(null);
  }

  isDisabled(row: WorkflowBindingRow): boolean {
    return !row.runtimeReady || row.missing;
  }

  isSelected(row: WorkflowBindingRow): boolean {
    return !!this.binding && this.bindingMatchesRow(this.binding, row);
  }

  statusLabel(row: WorkflowBindingRow): string {
    if (row.missing) {
      return 'Unavailable';
    }

    return row.runtimeReady ? 'Ready' : 'Runtime blocked';
  }

  statusClass(row: WorkflowBindingRow): string {
    if (row.missing) {
      return 'status-missing';
    }

    return row.runtimeReady ? 'status-ready' : 'status-blocked';
  }

  humanizeReason(reason: string): string {
    return reason.replace(/_/g, ' ');
  }

  private loadRows(): void {
    this.loading = true;
    this.toolsService.listTools({serviceId: 'shine-ai', signatureVersion: 'v1'}).subscribe({
      next: tools => {
        this.rows = this.buildRows(tools);
        this.reconcileSelectedBinding();
        this.loading = false;
      },
      error: err => {
        console.error('Failed to load workflow bindings', err);
        this.error = 'Could not load Connect workflow inventory.';
        this.rows = this.buildRows([]);
        this.reconcileSelectedBinding();
        this.loading = false;
      }
    });
  }

  private buildRows(tools: ConnectInternalAiTool[]): WorkflowBindingRow[] {
    const rows = tools.map(tool => ({
      rowId: `export:${tool.operationId}`,
      operationId: tool.operationId,
      workflowId: tool.workflowId,
      workflowName: tool.workflowName,
      workflowVersion: tool.workflowVersion,
      specHash: tool.specHash ?? '',
      description: tool.description,
      inputSchema: tool.inputSchema ?? '{}',
      runtimeReady: !!tool.readiness?.runtimeReady,
      blockingReasons: [...(tool.readiness?.blockingReasons ?? [])],
      missing: false
    }));

    if (this.binding && !rows.some(row => this.bindingMatchesRow(this.binding!, row))) {
      rows.unshift({
        rowId: `saved:${this.binding.operationId}:${this.binding.workflowId}:${this.binding.workflowVersion}:${this.binding.specHash}`,
        operationId: this.binding.operationId,
        workflowId: this.binding.workflowId,
        workflowName: '',
        workflowVersion: this.binding.workflowVersion,
        specHash: this.binding.specHash,
        description: this.savedBindingUnavailableDescription(rows, this.binding),
        inputSchema: this.binding.inputSchema,
        runtimeReady: false,
        blockingReasons: [rows.some(row => row.operationId === this.binding?.operationId) ? 'workflow_binding_changed' : 'operation_not_exported'],
        missing: true
      });
    }

    return rows;
  }

  private reconcileSelectedBinding(): void {
    if (!this.binding) {
      return;
    }

    const selectedRow = this.rows.find(row => this.bindingMatchesRow(this.binding!, row))
      ?? this.rows.find(row => row.rowId.startsWith('saved:'));
    if (!selectedRow) {
      return;
    }

    this.binding = this.toBinding(selectedRow);
    this.bindingChange.emit(this.binding);
  }

  private bindingMatchesRow(binding: IWorkflowFunctionBinding, row: WorkflowBindingRow): boolean {
    return binding.operationId === row.operationId
      && binding.workflowId === row.workflowId
      && binding.workflowVersion === row.workflowVersion
      && binding.specHash === row.specHash
      && binding.inputSchema === row.inputSchema;
  }

  private availabilityMessage(row: WorkflowBindingRow): string {
    if (row.missing) {
      return row.blockingReasons.includes('workflow_binding_changed')
        ? 'The saved Connect workflow binding changed. Refresh and re-select a ready workflow before saving or testing.'
        : 'The saved Connect workflow is no longer exported. Re-select a ready workflow before saving or testing.';
    }

    if (!row.runtimeReady) {
      return 'The selected Connect workflow is blocked in Connect and cannot be saved or tested until it is ready.';
    }

    return 'Connect workflow is ready.';
  }

  private savedBindingUnavailableDescription(rows: WorkflowBindingRow[], binding: IWorkflowFunctionBinding): string {
    return rows.some(row => row.operationId === binding.operationId)
      ? 'This saved workflow binding no longer matches the current Connect export.'
      : 'This saved workflow binding is no longer exported by Connect.';
  }
}
